import {useState,useCallback} from "react";
import {useNavigate} from "react-router-dom";
import {useSnackbar} from '../SnackbarContext.js';
import {useLoading} from '../LoadingContext';
import {consoleLog} from '../../utilities/Utilities.js';
import {useApplication} from '../ApplicationContext.js';
import {getUserNotifications} from '../../integration/Api.js';
import {updateObjectRecord,deleteObjectRecordsByIdList} from '../../integration/ObjectManager.js';
import NotificationsContext from "./NotificationsContext";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
const file="NotificationsProvider.js";

export const NotificationsProvider=({children})=>{
    const navigate=useNavigate();
    const {showLoading,hideLoading}=useLoading();
    const {showSnackbar}=useSnackbar();
    const [notifications,setNotifications]=useState([]);
    const [unread,setUnread]=useState(0);
    const [total,setTotal]=useState(0);
    const [loaded,setLoaded]=useState(0);
    const {globalData}=useApplication();
    const application={globalData,getLocale:(key,options)=>globalData?.getLocale(key,options)};
    const locale=application.getLocale;

    const refreshNotifications=useCallback(async()=>{
        try{
            const data=await getUserNotifications();
            consoleLog({file:file,context:"refreshNotifications",arg_name:"data",arg_value:data});
            setNotifications(data?.notifications || []);
            setUnread(data?.unread || 0);
            setTotal(data?.total || 0);
            setLoaded(data?.loaded || 0);

        }
        catch(err){
            consoleLog({file:file,context:"refreshNotifications error",arg_name:"err",arg_value:err});
            setNotifications([]);
            setUnread(0);
            setTotal(0);
            setLoaded(0);
        }
    },[]);

    const deleteNotification=useCallback(async(idList)=>{
        try{
            showLoading();
            const data=await deleteObjectRecordsByIdList({entityType:'Notifications',entityParams:['idList='+idList]});
            if('error' in data){
                showSnackbar(data.error,'error');
            } 
            else {
                showSnackbar(locale("NotificationsDeleted"),'success');
                refreshNotifications();
            }
        } 
        finally {
            hideLoading();
        }
    },[showLoading,hideLoading,showSnackbar,locale,refreshNotifications]);
  
    const navigateToNotification=useCallback((entityId)=>{
        navigate(`/my_notification/${entityId}`);
    },[navigate]);

    const updateNotification=useCallback(async(entityId)=>{
        const data=await updateObjectRecord({
            entityType:"Notifications",
            entityId,
            entityData:{read:true},
            getUpdatedRecord:false,
        });
        if("error" in data){
            showSnackbar(data.error, "error");
            return false;
        }
        else{
            refreshNotifications();
            return true;
        }
    },[showSnackbar,refreshNotifications]);

    const clickOnNotification=useCallback(async(n)=>{
        if(n.read){
            navigateToNotification(n.id);
        } 
        else{
            const result=await updateNotification(n.id);
            if(result){
                navigateToNotification(n.id);
            }
        }
    },[navigateToNotification,updateNotification]);

    const getNotificationIcon=(type)=>{
        switch(type){
            case "SUCCESS":
                return <CheckCircleOutlineIcon sx={{color:"success.main",fontSize:18}}/>;
            case "WARNING":
                return <WarningAmberIcon sx={{color:"warning.main",fontSize:18}}/>;
            case "ERROR":
                return <ErrorOutlineIcon sx={{color:"error.main",fontSize:18}}/>;
            case "INFO":
            default:
                return <InfoOutlinedIcon sx={{color:"info.main",fontSize:18}}/>;
        }
    };

    const getNotificationColor=(type)=>{
        switch(type){
            case "SUCCESS":
                return "success.main";
            case "WARNING":
                return "warning.main";
            case "ERROR":
                return "error.main";
            case "INFO":
            default:
                return "info.main";
        }
    };

    return(
        <NotificationsContext.Provider
            value={{
                clickOnNotification,
                navigateToNotification,
                updateNotification,
                deleteNotification,
                getNotificationIcon,
                getNotificationColor,
                refreshNotifications,
                notifications,
                unread,
                total,
                loaded
            }}>
            {children}
        </NotificationsContext.Provider>
    );
};